import { PATHS } from '@belong/common';
import {
  ListingApplicationApplicantModel,
  ResidentLeaseBasicInfo,
} from 'api/models';
import RenterAccountBookmark from 'models/renterAccounts/RenterAccountBookmark';
import { ApplicationScreen, ScreenResult } from './ApplicationStatusUtils';

export type SignerCurrentData = {
  residentType: ResidentLeaseBasicInfo['residentType'];
  signatureStatus: ResidentLeaseBasicInfo['signatureStatus'];
  imageUrl?: string;
  fullName: string;
  isCurrentUser: boolean;
};

export function resolveLeaseSignatureStatus(
  bookmark?: RenterAccountBookmark,
  currentApplicant?: ListingApplicationApplicantModel,
  signatureInfo?: {
    signMode?: string;
    hasResidentCompletedSigningFlow?: boolean;
    hasConciergeDownloadedFile?: boolean;
  },
  isSelfServe?: boolean,
): ScreenResult {
  // `bookmark` is declared optional but every caller passes one and this
  // destructure was already unconditional, so the assertion keeps the
  // existing throw rather than turning it into an empty result.
  const { leaseInfo } = bookmark!;

  const signMode =
    signatureInfo?.signMode || leaseInfo?.basicInfo?.signatureInfo?.signMode;
  const isNotarizedDocument = signMode === 'External';
  const residents = leaseInfo?.residents;

  const currentResident = residents?.find(
    (resident: any) =>
      resident.basicInfo.userInfo.userId === currentApplicant?.residentUniqueId,
  );

  if (currentResident) {
    const currentResidentType = currentResident.leaseInfo?.residentType;
    const primaryResident = residents?.find(
      (resident: any) => resident.leaseInfo.residentType === 'Primary',
    );
    const primaryResidentStatus = primaryResident?.leaseInfo?.signatureStatus;
    const currentResidentStatus = currentResident.leaseInfo?.signatureStatus;
    const allResidentsSigned = residents?.every(
      (resident: any) => resident.leaseInfo?.signatureStatus === 'Signed',
    );

    const signers: SignerCurrentData[] = residents?.map((resident: any) => ({
      signatureStatus: resident.leaseInfo?.signatureStatus,
      imageUrl: resident.basicInfo.userInfo.profileImageUrl,
      fullName: `${resident.basicInfo.userInfo.firstName} ${resident.basicInfo.userInfo.lastName}`,
      residentType: resident.leaseInfo?.residentType,
      isCurrentUser:
        resident.basicInfo.userInfo.userId ===
        currentApplicant?.residentUniqueId,
    }));

    if (
      (allResidentsSigned && isNotarizedDocument) ||
      (currentResidentType === 'Primary' &&
        currentResidentStatus === 'Signed' &&
        isNotarizedDocument)
    ) {
      return {
        screen: ApplicationScreen.WithdrawnFallback,
        screenNumber: 15,
        reason: 'listing.Withdrawn',
        stepNumber: 4,
        cardTitle: 'Waiting for Notarized\u00A0document.',
        signers,
      };
    }

    if (allResidentsSigned && isSelfServe) {
      return {
        screen: ApplicationScreen.WithdrawnFallback,
        screenNumber: 15,
        reason: 'listing.Withdrawn',
        stepNumber: 5,
        isSelfServe: true,
        cardTitle: 'Your lease is\u00A0signed!',
        cardDescription:
          'Belong will counter-sign shortly, bringing you one step closer to moving into your new\u00A0home.',
      };
    }

    if (allResidentsSigned) {
      return {
        screen: ApplicationScreen.WithdrawnFallback,
        screenNumber: 15,
        reason: 'listing.Withdrawn',
        stepNumber: 5,
      };
    }

    if (currentResidentType === 'Primary') {
      if (currentResidentStatus === 'Pending') {
        return {
          screen: ApplicationScreen.WithdrawnFallback,
          screenNumber: 15,
          reason: 'listing.Withdrawn',
          stepNumber: 4,
          cardTitle: 'Your Lease is ready to\u00A0sign.',
          signers,
          signingUrl: `${PATHS.LEASE_SIGNING_FLOW_V2}/GetStarted?useLatestStep=true`,
        };
      }
      if (currentResidentStatus === 'Signed' && !allResidentsSigned) {
        return {
          screen: ApplicationScreen.WithdrawnFallback,
          screenNumber: 15,
          reason: 'listing.Withdrawn',
          stepNumber: 4,
          cardTitle: 'We’re waiting for your other Residents to\u00A0sign.',
          signers,
        };
      }
    }

    if (currentResidentType !== 'Primary') {
      if (primaryResidentStatus === 'Pending') {
        return {
          screen: ApplicationScreen.WithdrawnFallback,
          screenNumber: 15,
          reason: 'listing.Withdrawn',
          stepNumber: 4,
          cardTitle:
            'Waiting for your Primary Resident to sign the\u00A0lease.',
          signers,
        };
      }
      if (currentResidentStatus === 'Signed' && !allResidentsSigned) {
        return {
          screen: ApplicationScreen.WithdrawnFallback,
          screenNumber: 15,
          reason: 'listing.Withdrawn',
          stepNumber: 4,
          cardTitle: 'We’re waiting for your other Residents to\u00A0sign.',
          signers,
        };
      }
      return {
        screen: ApplicationScreen.WithdrawnFallback,
        screenNumber: 15,
        reason: 'listing.Withdrawn',
        stepNumber: 4,
        cardTitle: 'Your Lease is ready to\u00A0sign.',
        signers,
        signingUrl: `${PATHS.LEASE_SIGNING_FLOW_V2}/GetStarted?useLatestStep=true`,
      };
    }
  }

  return {
    screen: ApplicationScreen.WithdrawnFallback,
    screenNumber: 15,
    reason: 'listing.Withdrawn',
    stepNumber: 4,
    cardTitle: 'Not Primary as',
    cardDescription:
      'Please sign your lease agreement to complete your application.',
  };
}
